import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'environment/environment';
import { ApiAbstractService } from './api-abstract.service';

import { QuickScanDetail } from 'modules/shared/models/quick-scan.models';
import { OngoingDueDiligenceQuery, OngoingDueDiligenceDetail } from 'modules/shared/models/ongoing-due-diligence.models';
import { BillableItem } from 'modules/shared/models/billable.models';
import { Crp } from 'modules/shared/models/crp.models';
import {
  SearchEngineTypeValue, SearchItem, SearchType, ResultItem, SearchEngineTypeLabel,
  QuickScanSearch } from 'modules/shared/models/screening.models';
import {
  SearchParrams, SearchResultResponse, SearchResultPayload, SearchValidity, PostScreeningPayload,
  BatchUpdateSearchResultPayload, QuickScansQuery
} from 'store/models/screening.models';
import { CommentDraft, CommentResponse } from 'modules/shared/models/comment.models';

interface UrlAndParrams {
  url: string;
  parrams: SearchParrams;
}

export const ASYNC_SEARCH_ENGINES: SearchEngineTypeValue[] = [
  SearchEngineTypeValue.DJ,
  SearchEngineTypeValue.DJ2,
  SearchEngineTypeValue.DJ3,
  SearchEngineTypeValue.TR,
  SearchEngineTypeValue.ARTEMISCAN,
];

@Injectable({
  providedIn: 'root'
})
export class ScreeningService extends ApiAbstractService {

  constructor(
    private httpClient: HttpClient,
  ) {
    super(environment.API_BASE);
  }

  // FIXME: Later on talk to BE guys to make API(name, url, params) more consistency
  // Set end url and parrams based in engine value
  // BE is using inconsistent name for internet search
  private setUrlAndParrams(engine: SearchEngineTypeValue, parrams: SearchParrams = {}): UrlAndParrams {
    const uap: UrlAndParrams = {
      parrams: parrams,
      url: ''
    };

    if (engine === SearchEngineTypeValue.INTERNET_SEARCH || engine === SearchEngineTypeValue.NLP) {
      uap.url = `internet_searches/`;
      uap.parrams.version = 'V2';
    } else if (engine === SearchEngineTypeValue.OWN_NAME) {
      uap.url = `own_name_searches/`;
    } else {
      parrams.engine_type = engine;
      uap.url = `searches/`;
    }
    return uap;
  }

  getQuickScanById(id: number): Observable<QuickScanDetail> {
    return this.httpClient.get<QuickScanDetail>(this.buildUrl(`/quickscan/records/${id}/`));
  }

  // Get all comments of a quick scan result
  getQuickScanCommentById(id: number): Observable<CommentResponse> {
    return this.httpClient.get<CommentResponse>(this.buildUrl(`/quickscan/records/${id}/posts/`));
  }

  // Submit a new comment to a quickscan result
  submitQuickScanCommentById(id: number, comment: CommentDraft): Observable<Comment> {
    return this.httpClient.post<Comment>(this.buildUrl(`/quickscan/records/${id}/posts/`), comment);
  }

  // Get quick scan by id, engine & parrams
  getQuickScan(id: number, engineType: SearchEngineTypeValue, parrams: SearchParrams): Observable<QuickScanSearch> {
    let url = `/quickscan/records/${id}/`;
    const uap = this.setUrlAndParrams(engineType, parrams);
    url += uap.url;
    return this.httpClient.get(this.buildUrl(url, uap.parrams))
      .pipe(
        map((res: SearchResultResponse) => ({
          ...res,
          engineType: engineType,
          searchType: SearchType.QUICKSCAN,
        }))
      );
  }

  getQuickScanCsv(): Observable<Blob> {
    return this.httpClient.get(this.buildUrl(`/quickscan/csv/`), {
      responseType: 'blob'
    });
  }

  // Record screening by id, engineType
  postScreening(payload: PostScreeningPayload, engineType: SearchEngineTypeValue, parrams?: SearchParrams): Observable<SearchItem> {
    let url = `/api/records/${payload.recordId}/`;
    const uap = this.setUrlAndParrams(engineType, parrams ? parrams : {});
    url += uap.url;
    uap.parrams.base_url = JSON.stringify(payload);
    return this.httpClient.post<SearchItem>(this.buildUrl(url), uap.parrams);
  }

  // Get screening by id, engine & parrams
  getScreening(id: number, engineType: SearchEngineTypeValue, parrams: SearchParrams): Observable<SearchResultResponse> {
    // let url = this.apiBase;
    let url = `/api/records/${id}/`;
    const uap = this.setUrlAndParrams(engineType, parrams);

    url += uap.url;
    return this.httpClient.get<SearchResultResponse>(this.buildUrl(url, uap.parrams))
      .pipe(
        map((res: SearchResultResponse) => ({
          ...res,
          id: id,
          engineType: engineType,
          searchType: SearchType.SCREENING,
        }))
      );
  }

  // update individual screening result macthed status
  patchSearchResult(payload: SearchResultPayload): Observable<ResultItem> {
    let searchUrl = 'search_results';
    if (payload && payload.isInternet) {
      searchUrl = 'internet_search_results';
    } else if (payload && payload.isOwnName) {
      searchUrl = 'own_name_search_results';
    }
    return this.httpClient.patch<ResultItem>(this.buildUrl(`/api/${searchUrl}/${payload.id}/`), { matched: payload.status });
  }

  // Update comment and matched status by batch
  batchUpdateSearchResult(payload: BatchUpdateSearchResultPayload): Observable<ResultItem> {
    let searchUrl = 'search_results';

    if (payload && payload.isInternet) {
      searchUrl = 'internet_search_results';
    } else if (payload && payload.isOwnName) {
      searchUrl = 'own_name_search_results';
    }

    return this.httpClient.put<any>(this.buildUrl(`/api/${searchUrl}/batch/`), payload.updateToSend);
  }

  // Get quick scan list
  getQuickScanList(queries: QuickScansQuery) {
    return this.httpClient.get(this.buildUrl(`/quickscan/search/`, queries));
  }

  getODDs(queries: OngoingDueDiligenceQuery) {
    return this.httpClient.get(this.buildUrl('/api/ongoing_due_diligence/', queries));
  }

  getCrpList(queries: Crp) {
    return this.httpClient.get(this.buildUrl('/api/customers/', queries));
  }

  getSearchesODD(id: number): Observable<SearchItem> {
    return this.httpClient.get<SearchItem>(this.buildUrl(`/api/searches/${id}/`));
  }

  getInternetSearchesODD(id: number): Observable<SearchItem> {
    return this.httpClient.get<SearchItem>(this.buildUrl(`/api/internet_searches/${id}/`)).pipe(
      map((res: SearchItem) => ({ ...res, engineType: SearchEngineTypeValue.INTERNET_SEARCH }))
    );
  }

  getOwnNameSearchesODD(id: number): Observable<SearchItem> {
    return this.httpClient.get<SearchItem>(this.buildUrl(`/api/own_name_searches/${id}/`)).pipe(
      map((res: SearchItem) => ({ ...res, engineType: SearchEngineTypeValue.OWN_NAME }))
    );
  }

  getODDById(id: number): Observable<OngoingDueDiligenceDetail> {
    return this.httpClient.get<OngoingDueDiligenceDetail>(this.buildUrl(`/api/ongoing_due_diligence/${id}`));
  }

  checkValidSearch(recordIds: number[], searchEngines: Array<BillableItem>): Observable<SearchValidity[]> {
    const searchObs: Array<Observable<SearchResultResponse>> = [];
    recordIds.forEach((id: number) => {
      searchEngines.map((searchEngine: BillableItem) => (searchEngine).serviceType)
        .forEach((engine: SearchEngineTypeValue) => {
          searchObs.push(
            this.getScreening(id, engine, { limit: 1 })
            .pipe(catchError((error) => {
              return of(<any>{
                count: 0,
                engineType: engine,
                searchType: 'SCREENING',
                next: null,
                prev: null,
                results: [],
                error: error.message || error
              });
            }))
            );
        });
    });

    return forkJoin(searchObs)
      .pipe(
        map((searches: Array<SearchResultResponse>) => searches.map((search: SearchResultResponse) => {
          return <SearchValidity>{
            record: search.id,
            valid: search.results.length > 0 ? search.results[0].valid : false,
            engine: search.engineType
          };
        }))
      );
  }

  /**
   * Get label to show on UI for the engineType
   * @method getSearchEngineLabel
   */
  getSearchEngineLabel(engineType: SearchEngineTypeValue): string {
    try {
      const engineTypeName = Object.keys(SearchEngineTypeValue).find((keyName: string) => SearchEngineTypeValue[keyName] === engineType);
      return SearchEngineTypeLabel[engineTypeName];
    } catch (error) {
      return 'Unknown';
    }
  }
}
